import { ReactElement } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'

import FormLayout from 'components/v2/FormLayout'
import Heading from 'components/v2/Heading'
import IconButton from 'components/v2/IconButton'
import { TrashIcon } from 'components/Icons/TrashIcon'
import {
  ICabinSetting,
  ICabinStrategiesFormProps,
  ICabinStrategy,
  ICabinStrategyCardProps
} from 'features/InventoryRules/components/CabinStrategiesForm/types'
import { SelectBookingClassMaxLoadFactor } from 'features/InventoryRules/components/SelectBookingClassMaxLoadFactor'
import { IDefaultCabinSetting } from 'features/InventoryRules/components/SelectBookingClassMaxLoadFactor/types'
import { device } from 'utils/style'

const Wrapper = styled.div`
  display: grid;
  gap: 24px;

  @media ${device.laptop} {
    grid-template-columns: 1fr 1fr;
  }
`

const DefaultStatus = styled.div`
  padding: 8px 16px;
  background-color: #eaeaff;
  color: #5d59d7;
  border-radius: 24px;
  font-size: 16px;
  font-weight: 500;
`

const CabinStrategyCard = ({
  cabinStrategy,
  strategyIndex,
  isDeletable,
  onRemoveStrategy,
  companyId,
  setCabinStrategy,
  touched,
  errors,
  onBlur,
  prefixId
}: ICabinStrategyCardProps): ReactElement => {
  const defaultCabinSetting: IDefaultCabinSetting = cabinStrategy.isDefault
    ? { isActive: true, maxLoadFactor: '' }
    : { isActive: false, maxLoadFactor: '' }
  const setCabinSettings = (cabinSettings: ICabinSetting[]): void => {
    setCabinStrategy({
      ...cabinStrategy,
      cabinSettings
    })
  }

  return (
    <FormLayout.GridItem>
      <FormLayout.CardHeader>
        <Heading.H4 small>
          <FormattedMessage
            id="inventoryRules.form.section.cabinStrategy.title"
            defaultMessage="Strategy {index}"
            values={{ index: strategyIndex + 1 }}
          />
        </Heading.H4>
        {cabinStrategy.isDefault && (
          <DefaultStatus>
            <FormattedMessage id="inventoryRules.form.section.cabinStrategy.default" defaultMessage="Default" />
          </DefaultStatus>
        )}
        {isDeletable && <IconButton icon={<TrashIcon />} onClick={onRemoveStrategy} />}
      </FormLayout.CardHeader>
      <SelectBookingClassMaxLoadFactor
        cabinSettings={cabinStrategy.cabinSettings}
        setCabinSettings={setCabinSettings}
        prefixId={`${prefixId}.cabinSettings`}
        errors={errors?.cabinSettings}
        touched={touched?.cabinSettings}
        companyId={companyId}
        defaultCabinSetting={defaultCabinSetting}
        onBlur={onBlur}
      />
    </FormLayout.GridItem>
  )
}

export const CabinStrategiesForm = ({
  cabinStrategies,
  setCabinStrategies,
  prefixId,
  touched,
  errors,
  onBlur,
  companyId
}: ICabinStrategiesFormProps): ReactElement => {
  return (
    <Wrapper>
      {cabinStrategies.map((cabinStrategy, index) => {
        const handleRemoveStrategy = (): void => {
          setCabinStrategies(
            cabinStrategies.filter((_, strategyIndex) => strategyIndex !== index),
            index
          )
        }

        const setCabinStrategy = (newCabinStrategy: ICabinStrategy): void => {
          setCabinStrategies(
            cabinStrategies.map((cabinStrategy, cabinStrategyIndex) => {
              if (cabinStrategyIndex === index) {
                return newCabinStrategy
              }

              return cabinStrategy
            })
          )
        }

        return (
          <CabinStrategyCard
            key={`${index}/${cabinStrategies.length}`}
            cabinStrategy={cabinStrategy}
            strategyIndex={index}
            isDeletable={!cabinStrategy.isDefault}
            onRemoveStrategy={handleRemoveStrategy}
            setCabinStrategy={setCabinStrategy}
            onBlur={onBlur}
            errors={errors?.[index]}
            touched={touched?.[index]}
            prefixId={`${prefixId}[${index}]`}
            companyId={companyId}
          />
        )
      })}
    </Wrapper>
  )
}
